import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@/shared/ui/SimpleSelect";
import type { MechanicListQuery } from "../api/client";

export type MechanicsSearch = {
  offset?: number;
  limit?: number;
  status?: MechanicListQuery["status"];
  q?: string;
  sortBy?: MechanicListQuery["sortBy"];
  sortOrder?: MechanicListQuery["sortOrder"];
};

const STATUS_OPTIONS: SimpleSelectOption[] = [
  { value: "all", label: "ສະຖານະທັງໝົດ" },
  { value: "pending_verification", label: "ລໍຖ້າກວດສອບ" },
  { value: "active", label: "ໃຊ້ງານປົກກະຕິ" },
  { value: "suspended", label: "ລະງັບ" },
];

export function MechanicsFilter() {
  const nav = useNavigate({ from: "/app/mechanics" });
  const search = useSearch({ from: "/app/mechanics" }) as MechanicsSearch;
  const [searchValue, setSearchValue] = useState(search.q ?? "");

  useEffect(() => {
    setSearchValue(search.q ?? "");
  }, [search.q]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    nav({
      search: {
        ...search,
        offset: 0,
        q: val.trim() || undefined,
      },
    });
  }, 400);

  const statusValue = search.status ?? "all";
  const hasFilter = !!search.q?.trim() || statusValue !== "all";

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຕາມຊື່, User ID ຫຼືເລກບັດປະຈຳຕົວ..."
            defaultValue={searchValue}
            onChange={(e) => {
              const v = e.target.value;
              debounced(v);
            }}
            className="h-8 sm:max-w-2xs"
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              nav({
                search: {
                  ...search,
                  offset: 0,
                  status: val as MechanicListQuery["status"],
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-40"
            options={STATUS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                search: {
                  ...search,
                  offset: 0,
                  q: undefined,
                  status: "all",
                },
              })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" />
            ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
